<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

import { PreparedDispatcherTour } from '@/types/delivery-management-dispatcher-overview-widget'
import { IncidentType, TransportObjectType } from '@/types/transport-object'

interface Props {
    tour: PreparedDispatcherTour
}

const props = defineProps<Props>()

const { t } = useI18n()

const shouldShowDelay = computed(() => {
    return props.tour.events.some((tourEvent) => {
        return tourEvent.estimatedAt
    })
})

function formatIncidentCounts(transportType: TransportObjectType): string {
    const incidentCounts: Record<string, number> = {}

    props.tour.incidents
        .filter((incident) => incident.transportObject.type === transportType)
        .forEach((incident) => {
            const type = t(IncidentType[incident.type])
            incidentCounts[type] = (incidentCounts[type] || 0) + 1
        })

    return Object.entries(incidentCounts)
        .map(([type, count]) => `${type}: ${count}`)
        .join(', ')
}
</script>

<template>
    <div
        class="w-1/4 max-w-[400px] min-w-52 shrink-0 bg-primary-100 dark:bg-gray-600 py-3 px-4 flex flex-col space-y-1.5 rounded-l-lg"
    >
        <div class="flex items-start justify-between gap-3">
            <span v-truncate-tooltip class="font-semibold truncate" v-text="tour.name" />
            <span
                v-if="tour.currentDriverName"
                v-truncate-tooltip
                class="text-right truncate"
                v-text="tour.currentDriverName"
            />
        </div>

        <div class="flex items-center justify-between gap-3 text-slate-800 dark:text-slate-200">
            <div class="flex items-center gap-2">
                <mdi:truck
                    v-if="tour.vehicles || tour.hasVehicleIncidents"
                    class="text-sm shrink-0"
                />
                <span
                    v-if="tour.vehicles"
                    v-truncate-tooltip
                    class="truncate"
                    v-text="tour.vehicles"
                />
                <span
                    v-if="tour.hasVehicleIncidents"
                    v-tooltip="formatIncidentCounts(TransportObjectType.Vehicle)"
                    class="w-3 aspect-square rounded-full bg-red-400 block"
                />
            </div>
            <div class="flex items-center gap-2">
                <mdi:truck-trailer
                    v-if="tour.units || tour.hasUnitIncidents"
                    class="text-sm shrink-0"
                />
                <span
                    v-if="tour.units"
                    v-truncate-tooltip
                    class="truncate text-right"
                    v-text="tour.units"
                />
                <span
                    v-if="tour.hasUnitIncidents"
                    v-tooltip="formatIncidentCounts(TransportObjectType.Unit)"
                    class="w-3 aspect-square rounded-full bg-red-400 block"
                />
            </div>
        </div>

        <div class="flex justify-between gap-3 text-xs text-slate-800 dark:text-slate-200 truncate">
            <span>
                <span class="uppercase" v-text="tour.isTourStarted ? t('start') : t('planned')" />
                <span class="font-semibold ml-1" v-text="tour.startLabel" />
            </span>
            <span class="text-right">
                <span
                    v-if="tour.endLabel || tour.expectedEndLabel"
                    class="uppercase"
                    v-text="t('end')"
                />
                <span v-if="tour.endLabel" class="font-semibold ml-1" v-text="tour.endLabel" />
                <span
                    v-if="tour.expectedEndLabel"
                    class="font-semibold ml-1"
                    v-text="`(${tour.expectedEndLabel})`"
                />
            </span>
        </div>

        <div
            v-if="shouldShowDelay"
            v-tooltip="tour.delay >= 0 ? t('delayExplanation') : t('aheadOfTimeExplanation')"
            class="text-xs block w-fit"
            :class="tour.delay >= 0 ? 'text-yellow-500' : 'text-green-500'"
        >
            <span
                class="font-medium uppercase"
                v-text="tour.delay >= 0 ? t('delay') : t('aheadOfTime')"
            />
            <span class="font-semibold ml-1" v-text="tour.delayText" />
        </div>

        <div
            class="flex items-baseline justify-between gap-3 text-xs text-slate-800 dark:text-slate-200 truncate"
        >
            <span v-tooltip="t('estimatedDrivingTimeExplanation')">
                <span class="uppercase" v-text="t('estimatedDrivingTime')" />
                <span class="font-semibold ml-1" v-text="tour.estimatedDrivingTime" />
            </span>
            <span
                v-if="tour.currentDrivingTime"
                v-tooltip="t('currentDrivingTimeExplanation')"
                class="text-right"
            >
                <span class="uppercase" v-text="t('currentDrivingTime')" />
                <span class="font-semibold ml-1" v-text="tour.currentDrivingTime" />
            </span>
        </div>
    </div>
</template>
